import {
  createMachine,
  MachineConfig,
  MachineOptions,
  assign,
  DoneInvokeEvent,
  cancel,
  raise
} from "xstate4"

import {
  Context,
  Schema,
  Transitions,
  GenericTransitionsWithRecordId,
  OrgFilterTransition,
  SaveTransition,
  InitOrgFilterTransition,
  DisplayedPropsTransition
} from "./types"
import {
  initOrgFilterState,
  addModalState,
  deleteModalState,
  editModalState,
  assignStudentsModalState,
  massDeleteModalState,
  detailsTrayState,
  loadingState,
  downloadDataState,
  undoDeleteTransition,
  assignSubjectsMenuState
} from "./states"
import {
  assignStudents,
  fetchClassById,
  fetchStudents,
  fetchUsers,
  SearchData
} from "../manageActions"
import { getSchoolYear } from "../../mp-utils/generic-utils"
import downloadUrl from "../../mp-core/helpers/downloadUrl"
import {
  AddOrgClassRequest,
  DeleteOrgClassRequest,
  EditOrgClassRequest,
  UndeleteOrgClassRequest,
  GetOrgClassesRequest,
  DeleteOrgClassesRequest,
  OrgRefScope,
  DownloadLink
} from "../../../types/gqlTypes/graphql"
import {
  addOrgClass,
  deleteOrgClass,
  deleteOrgClasses,
  editOrgClass,
  undeleteOrgClass
} from "../../../services/generic-services/org"
import {
  downloadOrgClasses,
  getOrgClasses
} from "../../../services/generic-services/org"
import { getScopeBasedOnOrgFilters } from "../../../services/generic-services/org/helpers/orgUtils"
import { getGetRequestParams, getPaginationParams } from "./helpers"
import { updateFiltersEvent } from "./events"
import { checkRequiredFilters, getTableOptions } from "../helpers"
import { ManagePages } from "../types"
import { Roles } from "../../mp-utils/generic-utils"
import storage, { UM_TABLE_COLUMNS } from "../../mp-core/helpers/storage"
import { Educator } from "../../mp-database"

const FETCH_DELAY = 300
const FILTER_CHECK_DELAY = 150

const initialContext: Context = {
  uid: null,
  /* App Filters */
  userRole: null,
  scope: null,
  orgFilter: {
    districtId: null,
    schoolId: null,
    teacherId: null,
    classId: null,
    studentId: null
  },
  hasRequiredFilters: false,
  /* Table pagination */
  page: 0,
  pageSize: 10,
  hasNextPage: false,
  hasPrevPage: false,
  /* Table sorting */
  sort: null,
  /* Common actions */
  search: null,
  openAddModal: false,
  openEditModal: false,
  openMassDeleteModal: false,
  openDeleteModal: false,
  openDetailsTray: false,
  loading: {
    data: false,
    add: false,
    edit: false,
    remove: false,
    restore: false,
    resendInvite: false,
    sendResetRequest: false,
    download: false,
    import: false,
    assignStudents: false
  },
  snackbar: null,
  snackbarMessageInfo: "",
  /* Classes action bar */
  propertyFilter: {
    grade: [],
    subject: [],
    courseCode: [],
    createdBy: [],
    teacher: [],
    eocSubject: []
  },
  /* Classes */
  rowFilter: null,
  classTableContent: null,
  selectedClassRecord: null,
  lastDeletedClassRecordId: null,
  massSelectClassRecordIds: [],
  assignAction: "",
  canAddClass: false,
  availableEocSubjects: null,
  teachers: [],
  openAssignStudentsModal: false,
  students: [],
  assignedStudents: [],
  displayedProperties: [],
  openAssignMenu: false,
  selectedSubjects: []
}

const config: MachineConfig<Context, Schema, Transitions> = {
  id: "ManageUserModules",
  initial: "initialize_org_filter",
  context: initialContext,
  on: {
    SELECT_DISTRICTS: {
      target: "districts_page"
    },
    SELECT_DISTRICT_ADMINS: {
      target: "district_admins_page"
    },
    SELECT_SCHOOLS: {
      target: "schools_page"
    },
    SELECT_SCHOOL_ADMINS: {
      target: "school_admins_page"
    },
    SELECT_TEACHERS: {
      target: "teachers_page"
    },
    SELECT_CLASSES: {
      target: "classes_page"
    },
    SELECT_STUDENTS: {
      target: "students_page"
    }
  },
  states: {
    initialize_org_filter: { ...initOrgFilterState },
    districts_page: {},
    district_admins_page: {
      on: {
        ...updateFiltersEvent
      }
    },
    schools_page: {
      on: {
        ...updateFiltersEvent
      }
    },
    school_admins_page: {
      on: {
        ...updateFiltersEvent
      }
    },
    teachers_page: {
      on: {
        ...updateFiltersEvent
      }
    },
    classes_page: {
      // on entering classes page, restore table displayed props.
      entry: {
        type: "restoreDisplayedProperties",
        payload: ManagePages.Classes
      },
      initial: "check_required_filter",
      on: {
        // "FETCH" can be sent from "loading" or "idle" state
        FETCH: {
          target: "classes_page.loading"
        },
        ...updateFiltersEvent,
        CHECK_REQUIRED_FILTER: {
          target: "classes_page.check_required_filter"
        },
        SET_DISPLAYED_PROPERTIES: {
          actions: [
            "setDisplayedProperties",
            { type: "saveDisplayedProperties", payload: ManagePages.Classes }
          ]
        }
      },
      states: {
        check_required_filter: {
          always: [
            {
              target: "loading",
              actions: assign<Context>({ hasRequiredFilters: true }),
              cond: ctx =>
                checkRequiredFilters(
                  ctx.userRole,
                  ManagePages.Classes,
                  ctx.orgFilter
                )
            },
            {
              target: "idle",
              actions: assign<Context>({ hasRequiredFilters: false })
            }
          ]
        },
        loading: { ...loadingState },
        download_data_transition: { ...downloadDataState },
        backToPrevious: {
          always: [
            {
              target: "details_tray",
              cond: ctx => ctx.openDetailsTray
            },
            {
              target: "idle"
            }
          ]
        },
        idle: {
          on: {
            UPDATE_PROPERTY_FILTERS: {
              // Start fetching data after some delay.
              actions: ["setPropertyFilters", "cancelFetch", "updateFetch"]
            },
            /* Action Bar events */
            SEARCH: {
              // Start fetching data after some delay.
              actions: ["setSearchData", "cancelFetch", "updateFetch"]
            },
            // We won't use "LOAD_NEXT_PAGE"/"LOAD_PREV_PAGE" for now,
            // but it will be usefull for infinite pagination.
            LOAD_NEXT_PAGE: {
              target: "loading",
              actions: "setNextPage",
              cond: "hasNextPage"
            },
            LOAD_PREV_PAGE: {
              target: "loading",
              actions: "setPrevPage",
              cond: "hasPrevPage"
            },
            LOAD_PAGE: {
              target: "loading",
              actions: "setPage"
            },
            SET_PAGE_SIZE: {
              target: "loading",
              actions: "setPageSize"
            },
            SORT: {
              target: "loading",
              actions: "setSort"
            },
            DOWNLOAD_DATA: {
              target: "download_data_transition"
            },
            OPEN_ADD: {
              target: "add_modal"
            },
            /* Class table row menu events */
            OPEN_VIEW_DETAILS: {
              target: "details_tray",
              actions: "selectedClassRecord"
            },
            OPEN_EDIT: {
              target: "edit_modal",
              actions: "selectedClassRecord"
            },
            OPEN_DELETE: {
              target: "delete_modal",
              actions: "selectedClassRecord"
            },
            UNDO_DELETE: {
              target: "undo_delete"
            },
            OPEN_ASSIGN_STUDENTS: {
              target: "assign_students_modal",
              actions: "selectedClassRecord"
            },
            OPEN_ASSIGN_SUBJECTS: {
              target: "assign_subjects_menu",
              actions: "selectedClassRecord"
            },
            VIEW_CLASS_STUDENTS: {
              /*
                This will go to the students_page,
                but for now it will just redirect until we have the student_page xState hooked up
              */
            },
            CHECK_ROW: {
              actions: "checkOrUncheckRow"
            },
            CHECK_ALL: {
              actions: "checkAllRows"
            },
            UNCHECK_ALL: {
              actions: "uncheckAllRows"
            },
            MASS_DELETE: {
              target: "mass_delete_modal"
            }
          }
        },
        add_modal: { ...addModalState },
        edit_modal: { ...editModalState },
        mass_delete_modal: { ...massDeleteModalState },
        details_tray: { ...detailsTrayState },
        delete_modal: { ...deleteModalState },
        undo_delete: { ...undoDeleteTransition },

        assign_students_modal: { ...assignStudentsModalState },
        assign_subjects_menu: { ...assignSubjectsMenuState }
      }
    },
    students_page: {
      on: {
        ...updateFiltersEvent
      }
    }
  },
  predictableActionArguments: true,
  preserveActionOrder: true
}

const getOptions = (
  actions = null,
  guards = null,
  services = null
): Partial<MachineOptions<Context, Transitions>> => ({
  actions: {
    // If multiple "FETCH" events are sent within delay interval,
    // cancel all except the last one to avoid sending too many requests.
    updateFetch: raise(
      { type: "FETCH" },
      {
        delay: FETCH_DELAY,
        id: "debounced-fetch"
      }
    ),
    cancelFetch: cancel("debounced-fetch"),

    // If OrgFilter quickly updated multiple times, send required only on last update.
    updateFilterCheck: raise(
      { type: "CHECK_REQUIRED_FILTER" },
      {
        delay: FILTER_CHECK_DELAY,
        id: "debounced-filter-check"
      }
    ),
    cancelFilterCheck: cancel("debounced-filter-check"),

    initOrgFilter: assign<Context, InitOrgFilterTransition>((_ctx, evt) => ({
      userRole: evt.userRole,
      uid: evt.uid,
      // Setup initial filters.
      orgFilter: evt.orgFilter,
      // Determine scope - the last orgFilter applied
      scope: getScopeBasedOnOrgFilters(
        evt.orgFilter.districtId,
        evt.orgFilter.schoolId,
        evt.orgFilter.teacherId,
        evt.orgFilter.classId,
        evt.orgFilter.studentId
      )
    })),
    setOrgFilters: assign<Context, OrgFilterTransition>(
      (ctx, { districtId, schoolId, teacherId, classId, studentId }) => ({
        orgFilter: {
          ...(districtId && { districtId: districtId }),
          ...(schoolId && { schoolId: schoolId }),
          ...(teacherId && { teacherId: teacherId }),
          ...(classId && { classId: classId }),
          ...(studentId && { studentId: studentId })
        },
        propertyFilter: {
          ...ctx.propertyFilter,
          // Reset "teacher" propertyFilter, if orgFilter "teacher" is applied.
          ...(teacherId && { teacher: [] })
        },
        // Reset page on filters change.
        page: 0,
        // Determine scope - the last orgFilter applied
        scope: getScopeBasedOnOrgFilters(
          null,
          districtId,
          schoolId,
          teacherId,
          classId,
          studentId
        )
      })
    ),
    setPropertyFilters: assign<
      Context,
      Extract<Transitions, { type: "UPDATE_PROPERTY_FILTERS" }>
    >((_ctx, { filters }) => ({
      propertyFilter: {
        grade: filters.grade ?? [],
        subject: filters.subject ?? [],
        courseCode: filters.courseCode ?? [],
        teacher: filters.teacher ?? [],
        eocSubject: filters.eocSubject ?? [],
        createdBy: filters.createdBy ?? []
      }
    })),
    setSearchData: assign<Context, Extract<Transitions, { type: "SEARCH" }>>(
      (_ctx, evt) => ({
        search: evt.value,
        // Reset page on search change.
        page: 0
      })
    ),
    setNextPage: assign<Context, Transitions>(ctx => ({ page: ctx.page + 1 })),
    setPrevPage: assign<Context, Transitions>(ctx => ({ page: ctx.page - 1 })),
    setPage: assign<Context, Extract<Transitions, { type: "LOAD_PAGE" }>>(
      (_ctx, evt) => ({ page: evt.page })
    ),
    setPageSize: assign<
      Context,
      Extract<Transitions, { type: "SET_PAGE_SIZE" }>
    >((_ctx, evt) => ({
      page: 0,
      pageSize: evt.pageSize
    })),
    setSort: assign<Context, Extract<Transitions, { type: "SORT" }>>(
      (_ctx, { name, asc }) => ({ sort: { name, asc } })
    ),
    selectedClassRecord: assign<Context, GenericTransitionsWithRecordId>(
      (ctx, evt) => {
        /* No need to find the record again if we still have selectedClassRecord */
        const getSelectedClassRecord =
          ctx.selectedClassRecord?.id === evt.recordId
            ? ctx.selectedClassRecord
            : ctx.classTableContent.find(record => record.id === evt.recordId)
        return {
          selectedClassRecord: getSelectedClassRecord
        }
      }
    ),
    checkOrUncheckRow: assign<Context, GenericTransitionsWithRecordId>(
      (ctx, evt) => {
        const isChecked = ctx.massSelectClassRecordIds.find(
          checkedRecordId => checkedRecordId === evt.recordId
        )
        if (isChecked) {
          const selectedWithoutRecordId = ctx.massSelectClassRecordIds.filter(
            checkedRecordId => checkedRecordId !== evt.recordId
          )
          return { massSelectClassRecordIds: selectedWithoutRecordId }
        }

        return {
          massSelectClassRecordIds: [
            ...ctx.massSelectClassRecordIds,
            evt.recordId
          ]
        }
      }
    ),
    checkAllRows: assign<Context>(ctx => ({
      massSelectClassRecordIds: (ctx.classTableContent ?? [])
        .filter(orgClass => !orgClass.meta.managedByIntegration)
        .map(orgClass => orgClass.id)
    })),
    uncheckAllRows: assign<Context>(() => ({
      massSelectClassRecordIds: []
    })),
    setDisplayedProperties: assign<Context, DisplayedPropsTransition>(
      (_ctx, evt) => ({
        displayedProperties: evt.properties
      })
    ),
    saveDisplayedProperties: (
      ctx,
      evt: DisplayedPropsTransition,
      meta: any
    ) => {
      try {
        storage.set(
          `${UM_TABLE_COLUMNS}_${meta.action.payload}_${ctx.uid}`,
          JSON.stringify(evt.properties)
        )
      } catch (error) {
        console.error("Unable to save displayed properties change.")
      }
    },
    restoreDisplayedProperties: assign((ctx, _evt, meta: any) => {
      let displayedProperties: string[]
      const pageName = meta.action.payload

      try {
        displayedProperties = JSON.parse(
          storage.get(`${UM_TABLE_COLUMNS}_${pageName}_${ctx.uid}`)
        )
      } catch (error) {
        console.error("Saved displayed properties not found or invalid.")
        displayedProperties = getTableOptions(pageName).displayedProperties
      }

      return {
        displayedProperties,
        sort: { name: "className", asc: true }
      }
    }),
    downloadByUrl: (_ctx: Context, evt: DoneInvokeEvent<DownloadLink>) => {
      return downloadUrl(evt.data.url, undefined, "_blank")
    },
    resetClassStudents: assign<Context>(() => ({
      students: [],
      assignedStudents: []
    })),
    ...actions
  },
  guards: {
    hasNextPage: (ctx: Context) => ctx.hasNextPage,
    hasPrevPage: (ctx: Context) => ctx.hasPrevPage,
    ...guards
  },
  services: {
    getClassRecords: async (ctx, _evt) => {
      const getOrgClassesRequest: GetOrgClassesRequest = {
        ...getGetRequestParams(ctx),
        ...getPaginationParams(ctx)
      }

      const requests = []

      if (ctx.orgFilter.districtId) {
        const teachersSearchData: SearchData = {
          id: ctx.orgFilter.schoolId || ctx.orgFilter.districtId,
          searchField: ctx.orgFilter.schoolId ? "school" : "schoolDistrict",
          whereOptions: [["role", "==", Roles.Teacher]]
        }

        requests.push(fetchUsers(teachersSearchData))
      }

      requests.push(getOrgClasses(getOrgClassesRequest))

      const [teachers, orgClasses] = await Promise.all(requests)

      const response = {
        orgClasses: orgClasses,
        teachers:
          (teachers as Educator[])
            ?.filter(item => !item?.deleted)
            .map(teacher => `${teacher.firstName} ${teacher.lastName}`) ?? []
      }

      return response
    },
    addClass: async (ctx: Context, evt: SaveTransition) => {
      const orgRefScope: OrgRefScope = {
        ...ctx.orgFilter,
        scope: ctx.scope
      }
      const addOrgClassRequest: AddOrgClassRequest = {
        orgRefScope,
        className: evt.data.className,
        period: evt.data.period,
        grades: evt.data.grades,
        courseCode: evt.data.courseCode,
        subject: evt.data.subject,
        eocSubjects: evt.data.eocSubjects
      }
      return addOrgClass(addOrgClassRequest)
    },
    editClass: async (
      ctx,
      evt: SaveTransition | Extract<Transitions, { type: "ASSIGN_SUBJECTS" }>
    ) => {
      const orgRefScope: OrgRefScope = {
        ...ctx.orgFilter,
        scope: "CLASS",
        classId: ctx.selectedClassRecord.id
      }

      if (evt.type === "ASSIGN_SUBJECTS") {
        const editOrgClassRequest: EditOrgClassRequest = {
          orgRefScope,
          className: ctx.selectedClassRecord.className,
          period: ctx.selectedClassRecord.period,
          eocSubjects: ctx.selectedSubjects
        }
        return editOrgClass(editOrgClassRequest)
      }

      const editOrgClassRequest: EditOrgClassRequest = {
        orgRefScope,
        className: evt.data.className,
        period: evt.data.period,
        grades: evt.data.grades,
        courseCode: evt.data.courseCode,
        subject: evt.data.subject,
        eocSubjects: evt.data.eocSubjects
      }
      return editOrgClass(editOrgClassRequest)
    },
    deleteClass: async (ctx, evt) => {
      const orgRefScope: OrgRefScope = {
        ...ctx.orgFilter,
        scope: "CLASS",
        classId: ctx.selectedClassRecord.id
      }
      const deleteOrgClassRequest: DeleteOrgClassRequest = {
        orgRefScope
      }
      return deleteOrgClass(deleteOrgClassRequest)
    },
    undeleteClass: async (ctx, evt) => {
      const orgRefScope: OrgRefScope = {
        ...ctx.orgFilter,
        scope: "CLASS",
        classId: ctx.lastDeletedClassRecordId
      }
      const undeleteOrgClassRequest: UndeleteOrgClassRequest = {
        orgRefScope
      }
      return undeleteOrgClass(undeleteOrgClassRequest)
    },
    massDeleteClasses: async (ctx, evt) => {
      if (ctx.massSelectClassRecordIds.length < 1) {
        throw Error("No class rows are selected. Unable to delete any classes.")
      }
      const orgRefScope: OrgRefScope = {
        ...ctx.orgFilter,
        scope: ctx.scope
      }
      const deleteOrgClassesRequest: DeleteOrgClassesRequest = {
        orgRefScope,
        classIds: ctx.massSelectClassRecordIds
      }
      return deleteOrgClasses(deleteOrgClassesRequest)
    },
    downloadClassData: async ctx => {
      return downloadOrgClasses(getGetRequestParams(ctx))
    },
    fetchClassStudents: async ctx => {
      const { districtId, schoolId } = ctx.orgFilter ?? {}
      const classId = ctx.selectedClassRecord?.id

      if (!classId) {
        throw new Error("No selected class.")
      }

      const classRecord = await fetchClassById(classId)

      if (!classRecord) {
        throw new Error("Class not found.")
      }

      // Fetch all students from school
      let students = await fetchStudents(districtId, schoolId)
      students = students.filter(student => !student.deleted)

      const assignedStudents = Object.entries(classRecord.students)
        .filter(([, value]) => value)
        .map(([key]) => students.find(student => student.id === key))
        .filter(student => student)

      return { students, assignedStudents }
    },
    assignStudents: async (
      ctx,
      evt: Extract<Transitions, { type: "SAVE" }>
    ) => {
      const startingAssigned = ctx.assignedStudents.length

      const selectedClassId = ctx.selectedClassRecord?.id
      const selectedClass = await fetchClassById(selectedClassId)

      const studentsData = {
        schoolYear: getSchoolYear().year
      }

      await assignStudents(
        evt.data.notAssigned,
        evt.data.assigned,
        selectedClass,
        studentsData
      )

      const endingAssigned = evt.data.assigned.length
      const assignedDiff = endingAssigned - startingAssigned

      let assignAction:
        | "assigned"
        | "unassigned"
        | "assigned and unassigned"
        | "no-change"

      if (assignedDiff > 0) {
        assignAction = "assigned"
      } else if (assignedDiff < 0) {
        assignAction = "unassigned"
      } else {
        const assignedSet = new Set(ctx.assignedStudents)
        const newlyAssignedSet = new Set(evt.data.assigned)

        if ([...assignedSet].every(student => newlyAssignedSet.has(student))) {
          assignAction = "no-change"
        } else {
          assignAction = "assigned and unassigned"
        }
      }

      ctx.assignAction = assignAction
    },
    ...services
  }
})

type PromiseFunctionType = {
  [key: string]: (ctx: any, evt: any) => Promise<{}>
}

const createManageUserModulesMachine = (
  actions: PromiseFunctionType | null = null,
  guards: PromiseFunctionType | null = null,
  services: PromiseFunctionType | null = null
) => {
  const options = getOptions(actions, guards, services)
  return createMachine<Context, Transitions>(config, options)
}

export default createManageUserModulesMachine
